import {ShoppingCartApplicationModel} from "../../../shoppingcart/js/ShoppingCartApplicationModel";
import {IOwayoDataModel} from "../../js/model";
import {ResourceManagerTask} from "./task";
import {ResourceLoaderConstructorArguments, ResourceLoader} from "./resourceLoader";
import {Resource, ResourceConstructorArguments} from "./resource";
import DEBUG from "../../js/DEBUG";
import {EventVerteiler} from "../eventVerteiler";
import {Produktresponse} from "../../js/databaseInterfaceClasses";
import {IResourceManager} from "./IResourceManager";
import {arrayElementByKeyValue, arrayIndexByKeyValue} from "../arrayHelper";

export class ResourceManager implements IResourceManager{
    private readonly taskQueue : ResourceManagerTask[] = [];
    resources : Resource[];

    constructor(model:IOwayoDataModel|ShoppingCartApplicationModel) {
        this.setResourceManagerOnDependents();
        this.resources = [];
        this.resources.push(new DataModelResource(model))

    }

    deleteResourceByName(resourceName: string): void {
        this.resources = this.resources.filter(r=>r.resourceName !== resourceName);
    }

    registerProduktPHPResource(data: Produktresponse, resourceNameSuffix="") {
        this.registerResource(
            {resourceName: "produkt.php"+resourceNameSuffix,
                invalidatedWhen: ResourceInvalidationTriggers.PRODUCTCHANGE,
                startingStati:["loaded"],
                data
            }
        );
    }

    private setResourceManagerOnDependents() {
        ResourceManagerTask.setResourceManager(this);
        Resource.setResourceManager(this);
        ResourceLoader.setResourceManager(this);
        EventVerteiler.setResourceManager(this);
    }

    getLoader(args: ResourceLoaderConstructorArguments):ResourceLoader {
        return new ResourceLoader(args);
    }

    createTask(taskName: string) {
        return new ResourceManagerTask({taskName: taskName});
    }

    static createTaskStatic(taskName : string) {
        return new ResourceManagerTask({taskName: taskName});
    }

    addOrExecuteTask (task : ResourceManagerTask):void {

        const index = arrayIndexByKeyValue(this.taskQueue, "taskName", task.taskName);
        if (index > -1) {
            if (DEBUG) {
                console.log("deleting old Task with same name " + task.taskName);
            }

            this.taskQueue.splice(index, 1);
        }

        if (this.taskPrerequisitesMet(task)) {
            task.execute(this.getDataForTask(task));
            return;
        }
        this.taskQueue.push(task);
    }
    public taskExists = taskname=>this.taskQueue.some(t=>t.taskName === taskname);
    public resourceExists = resourcename=>arrayElementByKeyValue(this.resources, "resourceName", resourcename) !== null;

    getOrRegisterResource (resourceName : string) {
        let resource = arrayElementByKeyValue(this.resources, "resourceName", resourceName);
        if (resource !== null) {
            if (DEBUG) {
                console.log("resource " + resourceName + " already registered");
            }
            return resource;
        }
        resource = new Resource({resourceName: resourceName});
        this.resources.push(resource);
        return resource;
    }


    registerResource (resourceArgs : ResourceConstructorArguments):Resource {
        const resource = new Resource(resourceArgs);

        if (arrayIndexByKeyValue(this.resources, "resourceName", resourceArgs.resourceName) > -1) {
            if (DEBUG) {
                console.log("resource " + resourceArgs.resourceName + " already registered");
            }
            return resource;
        }
        this.resources.push(resource);
        this.checkTasksAndExecute();
        return resource;
    }

    public registerLoadedResourceWithoutData(resourceName, invalidationTrigger = null as string|null) {
        const r = new Resource({resourceName:resourceName, startingStati:["loaded", "done"]});
        if(invalidationTrigger !== null) {
            r.invalidAfter(invalidationTrigger);
        }

        this.resources.push(r);
        this.checkTasksAndExecute();
    }

    appendStatus (resourceName : string, newStatus : string) {
        const resource = this.getResourceByName(resourceName);
        if (resource === null) {
            return;
        }
        resource.addState(newStatus);
        this.checkTasksAndExecute();
    }

    private taskPrerequisitesMet (task : ResourceManagerTask) {
        for (let i = 0, lenI = task.prerequisites.length; i < lenI; i++) {
            const resource = this.getResourceByName(task.prerequisites[i].resourceName);
            if (resource === null) {
                //console.log("resource not found: " + task.prerequisites[i].resourceName);
                return false;
            }
            if (!resource.meetsRequirements(task.prerequisites[i].prerequisites)) {
                return false;
            }
        }
        return true;
    }

    checkTasksAndExecute () {
        for (let i = this.taskQueue.length - 1; i > -1; i--) {
            const task = this.taskQueue[i];
            if (this.taskPrerequisitesMet(task)) {
                this.taskQueue.splice(i, 1);
                task.execute(this.getDataForTask(task));
                this.checkTasksAndExecute();
                return;
                //die Funktion muss hier neu aufgerufen werden, da innerhalb von task.execute die Queue verändert werden könnte
            }
        }
    }

    private getDataForTask (task : ResourceManagerTask) {
        const dataObject = {};
        for (let i = 0, lenI = task.prerequisites.length; i < lenI; i++) {
            const resourceState = this.getResourceByName(task.prerequisites[i].resourceName);
            if (resourceState === null) {
                if (DEBUG) {
                    console.error("could not find resource " + task.prerequisites[i].resourceName);
                }

                continue;
            }
            dataObject[task.prerequisites[i].resourceName] = resourceState.data;
        }
        return dataObject;
    }

    public getResourceByName (resourceName : string) : Resource {
        return arrayElementByKeyValue(this.resources, "resourceName", resourceName);
    }

    publishResource (resourceName : string, loadedResource) {
        const resource = this.getResourceByName(resourceName);
        if (resource === null) {
            console.error("Resource " + resourceName + " nicht gefunden");
            return;
        }
        resource.data = loadedResource;
    }



    private deleteInvalidatedResources(eventName :string) {
        for (let i = this.resources.length - 1; i > -1; i--) {
            if (this.resources[i].invalidatedWhen.indexOf(eventName) > -1) {
                this.resources.splice(i, 1);
            }
        }
    }

    private deleteInvalidatedTasks(eventName : string) {
        for (let i = this.taskQueue.length - 1; i > -1; i--) {
            if (this.taskQueue[i].invalidatedWhen.indexOf(eventName) > -1) {
                this.taskQueue.splice(i, 1);
            }
        }
    }

    executeInvalidation (eventName : string) {
        this.deleteInvalidatedResources(eventName);
        this.deleteInvalidatedTasks(eventName);
    }

    resourceHasState =(resourceName : string, state : string)=>{
        const resource = this.getResourceByName(resourceName);
        return resource === null ? false : resource.hasState(state);
    }
}

export enum ResourceInvalidationTriggers {
    PRODUCTCHANGE = "productChange",
    INITCOLORS = "initColors",
    USERCHANGED = "userChanged",
    DESIGNCHANGED = "designChanged",
    PARTSJSON_PARSED = "partsJSONParsed",
    SPORT_CHANGED = "sportChanged"
}

class DataModelResource extends Resource{
    private readonly root;

    constructor(root) {
        super({resourceName:"dataModel", type:"DataModelResource"});
        this.root = root;
    }

    addState () {
        console.log("addState of DataModelRequirement was called. That doesn't work that way!");
        return this;
    }

    hasState (stateString : string) {
        const stateRequirements = DataModelResource.processStateString(stateString);
        let subject;
        try {
            subject = this.getSubjectInModel(stateRequirements.treePathInModel);
        } catch (e) {
            console.error("could not walk the given TreePath " + stateRequirements.treePathInModel.join("."));
            return false;
        }

        switch (stateRequirements.condition) {
            case "toBeNotEmpty":
                return typeof(subject) !== "undefined" && subject !== null && subject !== "";
            case "toBeZero":
                return subject === 0 || subject === "0";
            case "toBeTrue":
                return subject === true;
            case "toBeFalse":
                return subject === false;
            default:
                if (DEBUG) {
                    console.log("unknown condition in DataModelRequirement:" + stateRequirements.condition);
                }
                return false;
        }
    }

    getSubjectInModel (treePathInModel : string[]) {
        let subject = this.root, i;

        i = 0;
        while (i < treePathInModel.length && typeof(subject) !== "undefined") {
            subject = subject[treePathInModel[i]];
            i++;
        }
        if (i < treePathInModel.length) {
            throw("treeWalkError");
        }
        return subject;

    }

    static processStateString (stateString : string) {
        const splitString = stateString.split(" ");
        const treePathInModel = splitString[0].split(".");
        splitString.splice(0, 1);
        const condition = splitString.join(" ").trim();
        return {
            treePathInModel: treePathInModel,
            condition: condition
        };
    }
}
